/**
 * Omnichannel TypeScript types
 */

// Client contact information for channel selection
export interface ClientContactInfo {
  id: number;
  source: string; // email, telegram, whatsapp, etc.
  peer_address: string;
  display_name: string | null;
  is_primary: boolean;
  last_used: string | null; // ISO datetime
}

// Conversation preview for client list
export interface ConversationPreview {
  client_id: number; // 0 for orphan messages
  client_name: string | null;
  client_company: string | null;

  // For orphan messages (client_id = 0)
  peer_address: string | null;

  // Last message info
  last_message_text: string | null;
  last_message_source: string | null;
  last_message_direction: string | null;
  last_message_at: string | null; // ISO datetime

  // Stats
  unread_count: number;
  draft_count: number;

  // Available channels
  sources: string[];
  contacts: ClientContactInfo[];
}

// Paginated conversation list response
export interface ConversationListResponse {
  conversations: ConversationPreview[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

// Message for omnichannel view
export interface OmnichannelMessage {
  id: number;

  // Source and routing
  source: string;
  peer_address: string;
  direction: 'incoming' | 'outgoing';

  // Content
  subject: string | null;
  body_text: string;
  body_html: string | null;
  attachments: any[] | null;

  // Classification
  message_class: string | null;
  priority: string | null;

  // Status flags
  is_draft: boolean;

  // Relationships
  client_id: number | null;
  client_contact_id: number | null;
  user_id: number | null;
  reply_to_message_id: number | null;

  // Timestamps
  created_at: string; // ISO datetime
  updated_at: string | null; // ISO datetime
}

// Paginated message list response
export interface MessageListResponse {
  messages: OmnichannelMessage[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

// Request to send a new message
export interface SendMessageRequest {
  client_id: number;
  client_contact_id: number;
  body_text: string;
  subject?: string;
  reply_to_message_id?: number;
}

// Request to update a draft
export interface DraftUpdateRequest {
  body_text: string;
  subject?: string;
}

// Request to send a draft
export interface DraftSendRequest {
  client_contact_id?: number;
}

// Request to update client name
export interface UpdateClientNameRequest {
  name: string;
  peer_address?: string;  // Required for orphan messages (client_id=0)
  source?: string;  // Required for orphan messages (client_id=0)
}

// Response with client info after update
export interface ClientInfoResponse {
  id: number;
  name: string | null;
  company: string | null;
}

// Source icon mapping
export const SOURCE_ICONS: Record<string, string> = {
  email: '📧',
  telegram: '✈️',
  whatsapp: '💬',
  slack: '💼',
  facebook: '👤',
  phone: '📞',
  http: '🌐',
  websocket: '🔌',
  other: '💭',
};

// Source color mapping for badges
export const SOURCE_COLORS: Record<string, string> = {
  email: 'bg-blue-100 text-blue-800',
  telegram: 'bg-sky-100 text-sky-800',
  whatsapp: 'bg-green-100 text-green-800',
  slack: 'bg-purple-100 text-purple-800',
  facebook: 'bg-indigo-100 text-indigo-800',
  phone: 'bg-yellow-100 text-yellow-800',
  http: 'bg-gray-100 text-gray-800',
  websocket: 'bg-orange-100 text-orange-800',
  other: 'bg-gray-100 text-gray-800',
};

// Get icon for source
export function getSourceIcon(source: string): string {
  // Handle tagged sources like "websocket:example.com"
  const baseSource = source.includes(':') ? source.split(':')[0] : source;
  return SOURCE_ICONS[baseSource] || SOURCE_ICONS.other;
}

// Get color class for source badge
export function getSourceColor(source: string): string {
  const baseSource = source.includes(':') ? source.split(':')[0] : source;
  return SOURCE_COLORS[baseSource] || SOURCE_COLORS.other;
}

// WebSocket events
export interface OmnichannelAuthResponse {
  status: 'success' | 'error';
  message: string;
  user_id?: number;
  user_email?: string;
  user_name?: string;
}

export interface OmnichannelNewMessageEvent {
  message_id: number;
  client_id: number;
  source: string;
  direction: string;
  body_text: string;
  created_at: string;
}

export interface OmnichannelDraftCreatedEvent {
  message_id: number;
  client_id: number;
  source: string;
  body_text: string;
  created_at: string;
}


